/**
 * WPS (Welding Procedure Specification) Transaction Component
 * 
 * OVERVIEW:
 * This component manages the complete lifecycle of WPS documents for both Groove and Overlay welding procedures.
 * WPS defines the variables that control welding operations to ensure consistent, quality welds that meet
 * code requirements and project specifications.
 * 
 * WELDING PROCEDURE TYPES:
 * 
 * 1. GROOVE WELDING:
 *    - Used for full penetration welds in structural applications
 *    - Requires complete joint preparation with beveled edges
 *    - Critical for pressure vessels, pipelines, and structural steel
 *    - Involves multiple passes: root, fill, and cap passes
 *    - Requires precise control of heat input and interpass temperature
 *    - Common applications: Pipe-to-pipe joints, structural connections
 * 
 * 2. OVERLAY WELDING:
 *    - Applied for surface enhancement and corrosion resistance
 *    - Deposits weld metal on base material surface
 *    - Used for cladding, hardfacing, and repair applications
 *    - Requires careful dilution control to maintain overlay properties
 *    - Critical for nuclear, chemical, and marine industries
 *    - Common applications: Reactor vessel cladding, valve seats, wear surfaces
 * 
 * COMPONENT FUNCTIONALITY:
 * 
 * TAB 1 - GENERAL:
 * - Project and WPS identification details
 * - Base material specifications and properties
 * - Joint design and preparation requirements
 * - Welding position and accessibility constraints
 * - Pre-weld and post-weld heat treatment requirements
 * - Essential variables that affect weld quality
 * 
 * TAB 2 - WELDING PROCEDURE:
 * - Detailed welding parameters for each pass
 * - Electrode/filler metal specifications
 * - Current, voltage, and travel speed settings
 * - Shielding gas composition and flow rates
 * - Interpass temperature control
 * - Cleaning and inspection requirements between passes
 * 
 * TAB 3 - WELDING PROCEDURE SPECIFICATION:
 * - Final WPS document generation
 * - Qualification record references (PQR)
 * - Approved welding variables and ranges
 * - Quality control and inspection criteria
 * - Welder qualification requirements
 * - Document approval and revision control
 * 
 * KEY FEATURES:
 * - Multi-tab interface for organized data entry
 * - Real-time validation of welding parameters
 * - Copy functionality for similar procedures
 * - Status-based access control (Draft/Approved/Superseded)
 * - Integration with welder qualification system
 * - Automatic WPS numbering and revision control
 * - Export capabilities for shop floor use
 * 
 * VALIDATION RULES:
 * - Essential variables must be within qualified ranges
 * - Base material compatibility checks
 * - Welding position limitations
 * - Heat input calculations and limits
 * - Filler metal compatibility verification
 * - Code compliance validation (ASME, AWS, etc.)
 * 
 * WORKFLOW:
 * 1. Create new WPS or copy from existing
 * 2. Define general parameters and joint details
 * 3. Specify detailed welding procedures
 * 4. Generate final WPS document
 * 5. Submit for engineering approval
 * 6. Release for production use
 * 7. Track usage in welding operations
 * 
 * INTEGRATION POINTS:
 ** - Connects to WI (Welding Instruction) system
 * - Interfaces with welder certification database
 * - Exports to production planning systems
 * - Integrates with quality management system
 * 
 * 
 * 
 * READONLY LOGIC ON COPY OPERATIONS:
 * 
 * DISABLEFIELDS FUNCTION IMPLEMENTATION:
 * The DisableFields function controls field editability based on three key factors:
 * 1. Action type (Copy, Edit, Add)
 * 2. WPS Status (Temp Saved, In Progress, Approved)
 * 3. User P&T Band level (Professional 'P' or Tactical 'T')
 * 
 * FUNCTION LOGIC:
 * ```javascript
 * export const DisableFields = (actualStatus, preparedBy, Status, Action, action) => {
 *   if (action === Action.ActionCopy) {
 *     return true;  // Always readonly during copy operations
 *   } else if (actualStatus === Status.StatusPending && 
 *             (sessionStorage.getItem("Band") !== "P" && 
 *              sessionStorage.getItem("Band") !== "T")) {
 *     return true;  // Readonly for non-P&T band users when status is pending
 *   } else {
 *     return false; // Editable for authorized users
 *   }
 * };
 * ```
 * 
 * READONLY RULES DURING COPY:
 * - ALL fields become readonly during copy operations (action === ActionCopy)
 * - This prevents accidental modifications to source document during copy process
 * - Copy operation creates new document with 'Temp Saved' status
 * - New document allows editing based on user P&T band permissions
 * - Original document readonly state is temporary during copy process
 * - Approval signatures are cleared and require re-approval in copied document
 * - Revision numbers are incremented automatically for copied documents
 * 
 * COPY OPERATION WORKFLOW:
 * 1. Source document validation and readonly lock (DisableFields returns true)
 * 2. Deep copy of all WPS parameters and details
 * 3. Status reset to 'Temp Saved' with cleared approvals
 * 4. New WPS number generation with revision increment
 * 5. Readonly release on source document (DisableFields returns false)
 * 
 * WPS STATUS LEVELS & DISABLEFIELDS BEHAVIOR:
 * 
 * 1. TEMP SAVED:
 *    - DisableFields returns false for P&T band users
 *    - Full editing capabilities for authorized users
 *    - Non-P&T band users may have restrictions
 *    - Can be saved multiple times without approval workflow
 * 
 * 2. IN PROGRESS (Status.StatusPending):
 *    - DisableFields returns true for non-P&T band users
 *    - Professional band ('P'): Fields remain editable
 *    - Tactical band ('T'): Fields remain editable
 *    - Non-P&T users: All fields become readonly
 *    - Approval/Reject buttons controlled separately
 * 
 * 3. APPROVED:
 *    - DisableFields returns false (allows editing for authorized users)
 *    - Additional approval logic controls actual field editability
 *    - Professional band can initiate revisions
 *    - Tactical band access controlled by additional business rules
 * 
 * P&T BAND SESSION STORAGE:
 * - User band level stored in sessionStorage.getItem("Band")
 * - 'P' = Professional Band (full privileges)
 * - 'T' = Tactical Band (operational privileges)
 * - Other values = Non-P&T users (restricted access)
 * 
 * FIELD-LEVEL READONLY LOGIC:
 * 
 * DISABLEFIELDS IMPACT ON UI COMPONENTS:
 * - isReadonly state: Set by DisableFields function result
 * - isBtnDisabled state: Controls navigation and action buttons
 * - Form field disabled attribute: Applied based on isReadonly state
 * 

 * 
 * FIELD EDITABILITY RULES:
 * - Copy Action: All fields readonly during copy process
 * - Pending Status + Non-P&T Band: All fields readonly
 * - P&T Band Users: Fields editable based on status and business rules
 * - Button States: Managed separately from field readonly state
 * - Navigation: Copy operations allow navigation, other readonly states may restrict
 * 
 * 
 * WPS STATUS-BASED VISIBILITY:
 * 
 * TEMP SAVED STATUS:
 * - All fields editable for document creator
 * - P&T band restrictions apply based on user level
 * - Save/Continue editing available
 * - No approval workflows active
 * 
 * IN PROGRESS STATUS:
 * - Limited editing based on P&T band level
 * - Professional band: Can modify all parameters
 * - Approval/Reject buttons visible to authorized users
 * - Status change requires appropriate P&T band authority
 * 
 * APPROVED STATUS:
 * - Read-only for all users except authorized approvers
 * - Professional band: Can initiate revisions
 * - Tactical band: View-only access
 * - Approval history visible to all users
 * - Revision control strictly enforced
 * 
 * 
 * CONDITIONAL DISPLAY RULES:
 * - Approval/Reject buttons: Visible only for Professional band users
 * - Field editability: Controlled by DisableFields function result
 * - Button states: Managed by isBtnDisabled state
 * - Navigation tabs: May be disabled based on action type
 * - Status indicators: Show current readonly state
 * 
 * WORKFLOW INTEGRATION:
 * - DisableFields called on component mount and action changes
 * - Readonly state propagated to all child components
 * - P&T band validation integrated with session management
 * - Status-based access control enforced at field level
 * - Copy operations temporarily override normal readonly rules
 */

import { useState, useRef, useEffect } from "react";
import { Tabs, Tab } from "react-bootstrap";
import WeldingProcedure from "./WeldingProcedure";
import General from "./General";
import WeldingProcedureSpec from "./WeldingProcedureSpec";
import { AddRowsDetailsoBj, DisableFields, createDetailObj } from "./WPSObjects/WPSCommonUtils";
import { useLocation } from "react-router-dom";
import { GridLoader } from "react-spinners";

import { MessageTypes, Action, Status } from "../utility/common/constants/CommonConstants";

import { getbyprojectno } from "../../Services/wps_groove-services";
import { getdetailsById } from "../../Services/wps_details-service";
import {

  WPSHeader

} from "./WPSObjects/WPSGroove_entity";

import { NavigationButtons } from "../utility/ui/navigation/TransactionNavigation/NavigationButtons";
import {
  showToast,
  executeChildFunction,
  handleNavigation,

  setupBeforeUnloadListeners,

} from "../utility/ui/navigation/TransactionNavigation/TransactionUtils";

const GrooveTransaction = () => {
  const [key, setKey] = useState(1);

  const firstChildRef = useRef();
  const secondChildRef = useRef();
  const thirdChildRef = useRef();

  const location = useLocation();

  const [loading, setLoading] = useState(true);

  const [copydata, setcopydata] = useState("");
  const [action, setAction] = useState(location.state?.action);
  const [originalaction] = useState(location.state?.action);
  const [WPStype] = useState(location.state?.WPStype);
  const [headerObj, setheaderObj] = useState(
    action === Action.ActionEdit || action === Action.ActionCopy
      ? location.state?.data
      : WPSHeader(WPStype)
  );
  const [IsEditedPage1, setIsEditedPage1] = useState(false);
  const [IsEditedPage2, setIsEditedPage2] = useState(false);
  const [IsEditedPage3, setIsEditedPage3] = useState(false);
  const [isReadonly, setReadOnly] = useState(false);
  const [isBtnDisabled, setBtnDisabled] = useState("");

  const [detailObj, setdetailObj] = useState([]);
  useEffect(() => {
    if (action === Action.ActionAdd) {
      let temparray = createDetailObj(1, WPStype);

      setdetailObj(temparray);
    }
  }, []);







  const fetchprojectbyid = async (IdprojectPK, IdprojectSK) => {
    const result = await getbyprojectno(WPStype, IdprojectPK, IdprojectSK);

    if (result !== undefined) setheaderObj(result[0]);
  };

  const fetchbyid = async (IddetailPK, IddetailSK, processOrder) => {
    const result = await getdetailsById(IddetailPK, IddetailSK);
    if (action !== Action.ActionAdd) {
      if (processOrder) {
        // processOrder = "WWWW,SMAW,1.76a," → split into ["WWWW", "SMAW", "1.76a"]
        const orderList = processOrder.split(",").filter(Boolean);
        // reorder result to match orderList sequence
        const sorted = orderList
          .map(process => result.find(item => item.WPSD_Process === process))
          .filter(Boolean);
        AddRowsDetailsoBj(sorted, setdetailObj);
      } else {
        AddRowsDetailsoBj(result, setdetailObj);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (action !== Action.ActionAdd) {
        const IdprojectPK = location.state?.data.WPSH_Project_No;
        const IdprojectSK = location.state?.data.WPSH_WPS_No;

        // For copy operations, ensure sequential loading
        if (action === Action.ActionCopy) {
          await fetchprojectbyid(IdprojectPK, IdprojectSK);
          await new Promise(resolve => setTimeout(resolve, 100));
          await fetchbyid(IdprojectPK, IdprojectSK, location.state?.data.WPSH_WPS_Process);
          setcopydata(true);
        } else {
          // For edit, load concurrently
          await Promise.all([
            fetchprojectbyid(IdprojectPK, IdprojectSK),
            fetchbyid(IdprojectPK, IdprojectSK, location.state?.data.WPSH_WPS_Process)
          ]);
        }

        headerObj.WPSH_UpdatedBy = (new Date()).toLocaleDateString('en-GB');
        headerObj.WPSH_UpdatedOn = sessionStorage.getItem("User");
      }

      setTimeout(() => {
        setLoading(false);
      }, action === Action.ActionCopy ? 700 : 500);
    };

    loadData();


  }, []);

  useEffect(() => {
    let read = DisableFields(headerObj.WPSH_Status, headerObj.WPSH_Prepared_By, Status, Action, action)
    if (action === Action.ActionCopy)
      setBtnDisabled("")
    else if (read)
      setBtnDisabled("disabled")
    else
      setBtnDisabled("")
    setReadOnly(read)
  }, [action])

  useEffect(() => {
    return setupBeforeUnloadListeners([IsEditedPage1, IsEditedPage2, IsEditedPage3]);
  }, [IsEditedPage1, IsEditedPage2, IsEditedPage3]);



  const next = async (e) => {
    const refs = [firstChildRef, secondChildRef, thirdChildRef];
    const funcNames = ['funcOnNext', 'funcOnNext2', 'funcOnNext'];
    const funcName = funcNames[key - 1];
    const res = await executeChildFunction(refs, key, funcName);
    showToast(res, key, setKey, 3, true);
  };

  const handleSelect = (e) => {
    Temp(e, "tab");
  };

  const Temp = async (e, tabaction) => {
    const refs = [firstChildRef, secondChildRef, thirdChildRef];
    const funcNames = ['funcOnNextTemp', 'funcOnNextTemp2', 'funcOnNextTemp'];
    const funcName = funcNames[key - 1];
    const res = await executeChildFunction(refs, key, funcName);

    showToast(res, key, setKey, 3, true);

    if (res === MessageTypes.Temp || res === MessageTypes.None) {
      handleNavigation(tabaction, e, key, setKey, 3);
    }
  };

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <div className="position-absolute ms-3" style={{ top: "15px", display: "flex" }}>
        <strong>Add New</strong>
        &nbsp;&nbsp;
        <span className="ms-1 warning-text" style={{ fontSize: "small", position: "relative", top: "3px" }}>
          (All fields are mandatory)
        </span>
        &nbsp;
        <div style={{ height: "30px", width: "425px" }} className="info_bg">
          <span className="ms-1 transnote info-text" style={{ fontSize: "small" }}>
            NOTE: Green color fields will be used for validation in WI session
          </span>
        </div>
      </div>
      <div className="border-divider" />
      <div className="mt-4">
        <div style={{ borderBottom: "2px solid var(--col-ddd)", position: "relative", top: "34px", zIndex: "-2" }}></div>
        <Tabs
          activeKey={key}
          onSelect={(e) => handleSelect(e)}
          className="mb-2 w-100 border-end"
          fill
        >

          <Tab
            eventKey={1}
            title={`General (${WPStype})`}
            className="bordertab"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
              borderBottom: "1px solid var(--col-ddd) "
            }}

          >
            <General
              ref={firstChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}


              detailObj={detailObj}
              setdetailObj={setdetailObj}
              WPStype={WPStype}
              tabno={key}
              setIsEditedPage1={setIsEditedPage1}
              isReadonly={isReadonly}
              setReadOnly={setReadOnly}
              action={action}
              setAction={setAction}
            />
          </Tab>



          <Tab
            eventKey={2}
            title="Welding Procedure"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
              width: "99%",
              borderBottom: "1px solid var(--col-ddd)"
            }}
            disabled={action === Action.ActionAdd}
          >
            <WeldingProcedure
              ref={secondChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              copydata={copydata}
              WPStype={WPStype}
              tabno={key}

              setIsEditedPage2={setIsEditedPage2}
              isReadonly={isReadonly}
              setReadOnly={setReadOnly}
              setAction={setAction}
              action={action}
            />
          </Tab>
          <Tab
            eventKey={3}
            title="Welding Procedure Specification"
            style={{
              height: window.innerHeight - 180,
              overflowY: "auto",
              overflowX: "hidden",
              borderBottom: "1px solid var(--col-ddd)"

            }}
            disabled={action === Action.ActionAdd}
          >
            <WeldingProcedureSpec
              ref={thirdChildRef}
              headerObj={headerObj}
              setheaderObj={setheaderObj}
              detailObj={detailObj}
              setdetailObj={setdetailObj}
              copydata={copydata}
              WPStype={WPStype}
              setKey={setKey}
              tabno={key}

              setIsEditedPage3={setIsEditedPage3}
              setBtnDisabled={setBtnDisabled}
              isReadonly={isReadonly}
              setReadOnly={setReadOnly}
              action={action}
              setAction={setAction}
            />
          </Tab>
        </Tabs>


        <NavigationButtons
          currentKey={key}
          maxTabs={3}
          isBtnDisabled={isBtnDisabled}
          originalAction={originalaction}
          onNext={next}
          onTemp={Temp}
        />



      </div>
    </>
  );
};

export default GrooveTransaction;
